import React, { useContext, useEffect } from "react";
import { Checkbox, Paper } from "@mui/material";

import { Node } from "ui";

import { selectLayerByVersionAndLayerKey, selectVersionConfig } from "fond/api";
import { LayerConfig } from "fond/types/ProjectLayerConfig";
import { useAppSelector } from "fond/utils/hooks";

import { InferredIcon } from "../Legend";
import { MapContext } from "../MapProvider";

import { Container, Label, Row } from "./LayerSelection.styles";

export interface IButtonProps {
  title?: string;
  icon?: React.ReactNode;
  mode?: string;
}

const LayerSelection: React.FC = () => {
  const { drawControl, isDrawing } = useContext(MapContext);
  const versionId = useAppSelector((state) => state.project.versionId);
  const config = useAppSelector((state) => selectVersionConfig(state, versionId));
  const [selectedLayers, setSelectedLayers] = React.useState<string[]>(drawControl.current?.options?.editingLayerIds || []);

  /**
   * Generates a list of editable layers based on the drawing control's option.allLayerIds and the project's version config.
   * Each layer is represented as a node with a value, label, and icon for use in the checkbox tree.
   */
  const editableLayers = useAppSelector((state) => {
    const layerConfigs: Node[] = [];
    const layerIds = drawControl.current?.options?.allLayerIds;

    if (!layerIds || !config) return layerConfigs;

    layerIds.forEach((layerId) => {
      const layerConfigID = selectLayerByVersionAndLayerKey(state, { versionId, layerId })?.Configuration.ID;
      const layerConfig = layerConfigID ? (config.Data.entities[layerConfigID] as LayerConfig) : undefined;
      if (layerConfig) {
        layerConfigs.push({
          value: layerId,
          label: layerConfig?.Label,
          icon: <InferredIcon entity={layerConfig} config={config} />,
        });
      }
    });

    return layerConfigs;
  });

  useEffect(() => {
    setSelectedLayers(drawControl.current?.options?.editingLayerIds || []);
  }, [drawControl, drawControl.current?.options?.editingLayerIds]);

  /**
   * Handles the event when layers are checked/selected in the checkbox tree.
   * Updates the drawing control's editing layer IDs.
   * @param nodeIds - The IDs of the layers that have been checked/selected.
   */
  const handleOnCheck = (id: string, checked: boolean) => {
    if (drawControl.current && drawControl.current.options) {
      drawControl.current.options.editingLayerIds = checked ? [...selectedLayers, id] : selectedLayers.filter((layerId) => layerId !== id);
      setSelectedLayers(drawControl.current.options.editingLayerIds);
    }
  };

  if (!isDrawing || !drawControl.current?.options?.allLayerIds) return null;

  return (
    <Container>
      <Paper sx={{ p: 1 }} elevation={3}>
        {editableLayers.map(({ label, value, icon }) => (
          <Row key={value}>
            <Checkbox
              id={value}
              checked={selectedLayers.includes(value)}
              onChange={(_, checked) => handleOnCheck(value, checked)}
              size="small"
              sx={{ p: 0.5 }}
            />
            <Label htmlFor={value}>
              {icon}
              {label}
            </Label>
          </Row>
        ))}
      </Paper>
    </Container>
  );
};

export default LayerSelection;
